import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const CARRIER_COLORS = [
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#0097a7', '#c62828', '#2e7d32', '#e65100', '#4527a0',
];

export interface SeriesPoint {
  timestamp: string;
  value: number;
  carrier: string;
}

interface SignalTimeSeriesProps {
  title: string;
  lteSeries: SeriesPoint[];
  nrSeries: SeriesPoint[];
  carriers: string[];
  yLabel: string;
  yDomain?: [number, number];
  timeAxis?: boolean;
}

// Each carrier gets its own column + parallel _ts key so tooltip shows real capture time
function buildChartData(
  series: SeriesPoint[],
  carriers: string[],
): Record<string, string | number | null>[] {
  const byCarrier = new Map<string, SeriesPoint[]>();
  carriers.forEach(c => byCarrier.set(c, []));
  series.forEach(p => byCarrier.get(p.carrier)?.push(p));
  carriers.forEach(c => {
    byCarrier.get(c)!.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  });

  // Row count is driven by the longest carrier series so no data is dropped
  const maxLen = Math.max(...carriers.map(c => byCarrier.get(c)!.length), 0);

  return Array.from({ length: maxLen }, (_, i) => {
    const row: Record<string, string | number | null> = { sampleIndex: i };
    carriers.forEach(c => {
      const pts = byCarrier.get(c)!;
      row[c] = pts[i]?.value ?? null;
      // Parallel timestamp key used by CustomTooltip on hover
      row[`${c}_ts`] = pts[i]?.timestamp ?? null;
    });
    return row;
  });
}

// Gaps larger than this between consecutive timestamps are treated as a campaign boundary
const CAMPAIGN_GAP_MS = 60 * 60 * 1000; // 1 hour

interface TimeChartResult {
  rows: Record<string, string | number | null>[];
  // Start/end sample indices for each detected campaign segment, used as x-axis tick labels
  campaignTicks: Array<{ index: number; label: string }>;
}

// Merges all carrier points into a gapless sequential index; real timestamps preserved in _ts keys
function buildChartDataByTime(series: SeriesPoint[], carriers: string[]): TimeChartResult {
  const byCarrier = new Map<string, SeriesPoint[]>();
  carriers.forEach(c => byCarrier.set(c, []));
  series.forEach(p => byCarrier.get(p.carrier)?.push(p));

  const allTsSet = new Set<number>();
  carriers.forEach(c => {
    byCarrier.get(c)!.forEach(p => allTsSet.add(new Date(p.timestamp).getTime()));
  });
  const allTs = Array.from(allTsSet).sort((a, b) => a - b);
  const tsToIndex = new Map<number, number>(allTs.map((ts, i) => [ts, i]));

  const lookup = new Map<string, Map<number, number>>();
  carriers.forEach(c => {
    const m = new Map<number, number>();
    byCarrier.get(c)!.forEach(p => m.set(new Date(p.timestamp).getTime(), p.value));
    lookup.set(c, m);
  });

  const rows = allTs.map(ts => {
    const row: Record<string, string | number | null> = { sampleIndex: tsToIndex.get(ts)! };
    carriers.forEach(c => {
      row[c] = lookup.get(c)?.get(ts) ?? null;
      // Parallel timestamp key used by CustomTooltip on hover
      row[`${c}_ts`] = lookup.get(c)?.has(ts) ? new Date(ts).toISOString() : null;
    });
    return row;
  });

  // Mark the first and last index of each campaign segment as labeled x-axis ticks
  const fmt = (ms: number) => new Date(ms).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const campaignTicks: Array<{ index: number; label: string }> = [];
  if (allTs.length > 0) {
    campaignTicks.push({ index: 0, label: fmt(allTs[0]) });
    for (let i = 1; i < allTs.length; i++) {
      if (allTs[i] - allTs[i - 1] > CAMPAIGN_GAP_MS) {
        campaignTicks.push({ index: i - 1, label: fmt(allTs[i - 1]) });
        campaignTicks.push({ index: i, label: fmt(allTs[i]) });
      }
    }
    const lastIdx = allTs.length - 1;
    if (campaignTicks[campaignTicks.length - 1].index !== lastIdx) {
      campaignTicks.push({ index: lastIdx, label: fmt(allTs[lastIdx]) });
    }
  }

  return { rows, campaignTicks };
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

// Replaces Recharts default tooltip; shows signal value + actual capture timestamp per carrier
function CustomTooltip({
  active,
  payload,
  yLabel,
  colorMap,
}: TooltipContentProps & {
  yLabel: string;
  colorMap: Record<string, string>;
}) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ background: 'white', border: '1px solid #ccc', p: 1, borderRadius: 1, fontSize: 12 }}>
      {payload.map(entry => {
        const name = String(entry.name ?? '');
        const row = (entry.payload ?? {}) as Record<string, unknown>;
        const ts = row[`${name}_ts`] as string | null;
        return (
          <Box key={name} sx={{ color: colorMap[name] ?? '#000', mb: 0.5 }}>
            <strong>{name}</strong>: {entry.value} {yLabel}
            {ts && <><br /><span style={{ color: '#666' }}>{formatTimestamp(ts)}</span></>}
          </Box>
        );
      })}
    </Box>
  );
}

function TechChart({
  label,
  series,
  carriers,
  yLabel,
  yDomain,
  timeAxis,
}: {
  label: string;
  series: SeriesPoint[];
  carriers: string[];
  yLabel: string;
  yDomain?: [number, number];
  timeAxis?: boolean;
}) {
  const activeCarriers = useMemo(
    () => carriers.filter(c => series.some(p => p.carrier === c)),
    [carriers, series],
  );

  // Unknown carriers hidden by default; user can toggle them on via chips
  const [visibleCarriers, setVisibleCarriers] = useState<Set<string>>(
    () => new Set(activeCarriers.filter(c => !c.toLowerCase().startsWith('unknown'))),
  );

  // Stable color assignment keyed by position in the full carriers list
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    carriers.forEach((c, i) => { map[c] = CARRIER_COLORS[i % CARRIER_COLORS.length]; });
    return map;
  }, [carriers]);

  const { data, campaignTicks } = useMemo(() => {
    if (timeAxis) {
      const result = buildChartDataByTime(series, activeCarriers);
      return { data: result.rows, campaignTicks: result.campaignTicks };
    }
    return { data: buildChartData(series, activeCarriers), campaignTicks: [] as TimeChartResult['campaignTicks'] };
  }, [series, activeCarriers, timeAxis]);

  const xTicks = useMemo(() => {
    if (data.length === 0) return [];
    if (timeAxis) return campaignTicks.map(t => t.index);
    return Array.from({ length: Math.floor(data.length / 100) + 1 }, (_, i) => i * 100);
  }, [data.length, timeAxis, campaignTicks]);

  const tickLabelMap = useMemo(
    () => new Map(campaignTicks.map(t => [t.index, t.label])),
    [campaignTicks],
  );

  const toggleCarrier = (carrier: string) => {
    setVisibleCarriers(prev => {
      const next = new Set(prev);
      if (next.has(carrier)) next.delete(carrier);
      else next.add(carrier);
      return next;
    });
  };

  if (data.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" mb={1}>{label}</Typography>
      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
        {activeCarriers.map(carrier => (
          <Chip
            key={carrier}
            label={carrier}
            size="small"
            onClick={() => toggleCarrier(carrier)}
            sx={{
              backgroundColor: visibleCarriers.has(carrier) ? colorMap[carrier] : 'transparent',
              color: visibleCarriers.has(carrier) ? 'white' : colorMap[carrier],
              border: `1px solid ${colorMap[carrier]}`,
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          />
        ))}
      </Stack>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={data} margin={{ top: 5, right: 60, left: 70, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="sampleIndex"
            ticks={xTicks}
            tickFormatter={(idx: number) => timeAxis ? (tickLabelMap.get(idx) ?? '') : String(idx)}
            angle={timeAxis ? -35 : 0}
            textAnchor={timeAxis ? 'end' : 'middle'}
            height={timeAxis ? 60 : 30}
            tick={{ fontSize: timeAxis ? 14 : 12 }}
            label={timeAxis ? undefined : { value: 'Sample #', position: 'insideBottom', offset: -15 }}
          />
          <YAxis
            label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: -5 }}
            domain={yDomain ?? ['auto', 'auto']}
          />
          <Tooltip
            content={(props) => (
              <CustomTooltip
                {...(props as TooltipContentProps)}
                yLabel={yLabel}
                colorMap={colorMap}
              />
            )}
          />
          {activeCarriers.filter(c => visibleCarriers.has(c)).map(carrier => (
            <Line
              key={carrier}
              type="monotone"
              dataKey={carrier}
              stroke={colorMap[carrier]}
              dot={false}
              connectNulls={!!timeAxis}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default function SignalTimeSeries({
  title,
  lteSeries,
  nrSeries,
  carriers,
  yLabel,
  yDomain,
  timeAxis,
}: SignalTimeSeriesProps) {
  return (
    <Box>
      <Typography variant="h6" mb={2}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <TechChart
          label="LTE 4G"
          series={lteSeries}
          carriers={carriers}
          yLabel={yLabel}
          yDomain={yDomain}
          timeAxis={timeAxis}
        />
        <TechChart
          label="NR 5G"
          series={nrSeries}
          carriers={carriers}
          yLabel={yLabel}
          yDomain={yDomain}
          timeAxis={timeAxis}
        />
      </Box>
    </Box>
  );
}
